/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { App, Tooltip } from 'antd'
import {
  CopyOutlined,
  NotificationOutlined,
  PlusOutlined,
  SendOutlined,
} from '@ant-design/icons'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import type { ReactNode } from 'react'
import { balanceLabel, balanceText } from '../utils/balanceI18n'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import colors from '@sportninja/common/constants/appColors'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { paragraphXSmall } from 'src/components/css'
import type { BalanceRegistration } from '@sportninja/common/services/teamBalance'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { CancelledToggleState } from '../utils/cancelledToggleState'
import type { BalanceRowFilter } from '../utils/balanceRowFilter'

export const ALL_DIVISIONS = 'all'

/**
 * "Show: Owes Money" — the closed select has to say what it filters, or it reads
 * as an unlabelled "All" floating in the toolbar.
 */
const renderRowFilterLabel = ({ label }: { label: ReactNode }) => (
  <span>
    <span
      css={css`
        color: ${colors.NEUTRAL_70};
      `}
    >
      {balanceLabel('common:balanceRowFilterLabel')}:{' '}
    </span>
    {label}
  </span>
)

/**
 * `SwitchInput` renders its label and its `Switch` as a FRAGMENT, so whatever
 * gap the PARENT sets is what separates them. The switch therefore gets its own
 * 8px wrapper so the pair reads as one control.
 */
const switchFieldStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

/**
 * Advisory, so it is deliberately quieter than the controls either side of it —
 * one step down in colour, no border, no background. It is a caption on the
 * Split Evenly button, not a control.
 */
const evenSplitStyle = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_70};
  white-space: nowrap;
`

const toolbarStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

/** A row breaks only BETWEEN its two groups, and only when a phone makes it. */
const toolbarRowStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`

const toolbarGroupStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`

/** Right-aligned, and stays right-aligned when a narrow row drops it below. */
const trailingGroupStyle = css`
  margin-left: auto;
  justify-content: flex-end;
`

/** The action buttons keep the header-action row's 8px gap. */
const actionGroupStyle = css`
  gap: 8px;
`

type BalanceControlsProps = {
  registrations: BalanceRegistration[]
  /** Design-ref quick filters: All / Owes money / Paid up / Open slots. */
  rowFilter: BalanceRowFilter
  onRowFilter: (value: BalanceRowFilter) => void
  /**
   * SN-4709 (James, Jul 31) — sheet-level actions replace the two toggles that
   * used to live here.
   *
   * `showSheetActions` is the old `showChips`: they only make sense against ONE
   * sheet, so they are hidden on the mixed All-Divisions view.
   *
   * `selectedCount` is how many rows are ticked in the grid. It is the ONLY
   * thing that reveals "Send N Invoices" — a bulk action with no selection is a
   * button that can only disappoint.
   */
  showSheetActions: boolean
  sheetActionsPending: boolean
  onSplitEvenly: () => void
  /**
   * SN-4709 (James, Aug 12) — the even share, already FORMATTED (e.g. "$120.00").
   *
   * This is the EVEN SPLIT column's replacement. James: "maybe get rid of the
   * column because it's actually not real, real data. It's just a reference…
   * maybe there's just a reference here to say it would be 120 per person." So
   * it is stated once, beside the button that applies it, instead of repeated
   * down a column of sortable numbers that read like money owed.
   *
   * Null on All-Divisions (an average across several fees means nothing) and on
   * a sheet with nothing to split — the line is then simply absent.
   */
  evenSplitLabel: string | null
  selectedCount: number
  onSendInvoices: () => void
  selectedRegistrationId: string
  onSelectRegistration: (id: string) => void
  /**
   * SN-4709 (Sep 1) — the admin Registration Details modal pins this sheet to
   * ONE registration, so the division filter is not a choice there. Hidden
   * rather than disabled: a greyed select that can only ever hold one value is
   * chrome describing a decision nobody is making.
   */
  hideDivisionFilter?: boolean
  showCancelled: boolean
  onToggleCancelled: (value: boolean) => void
  /**
   * SN-8346 — whether flipping Display Cancelled would reveal anything, and why
   * not. Computed in `BalanceTab` from the same registrations the sheet reads.
   */
  cancelledToggle: CancelledToggleState
  /**
   * Opens Add Player Slots. Which kind of slot — open, Player Finder or held for a
   * named person — is a tab inside it (Inon, Oct 2), not a menu here.
   */
  onAddSlots: () => void
  addDisabled: boolean
  /**
   * SN-4709 (James, Aug 12) — ONE Notify button, not a menu of three.
   *
   * "It's just they're the same thing. It's just different ways of notifying
   * people… it's just one mechanism and you can do multiple." Remind / Email /
   * Post & Notify were three doors to the same room, so the choice of channel
   * moved INSIDE the composer as checkboxes and the toolbar keeps one action.
   *
   * The audience is the row selection when there is one and the whole remindable
   * roster otherwise (James: "you just have to select first. That's fine."), so
   * the button never needs a second variant either.
   */
  onNotify: () => void
  /**
   * SN-4709 Team Privacy (Jeff, Sep 1) — the rep's control over who can join.
   *
   * Both are null on the All-Divisions view: privacy and the share link belong to ONE
   * registration, and averaging them across divisions would be meaningless. They ride
   * alongside `showSheetActions`, which already encodes "exactly one sheet is selected".
   *
   * `shareLink` is SERVER-BUILT (the same builder the Review step and the confirmation
   * email are served from). Composing it here would be a fourth definition of the URL.
   */
  isPrivate?: boolean | null
  onTogglePrivacy?: (value: boolean) => void
  shareLink?: string | null
}

/**
 * Two toolbar rows, each a leading and a trailing group (Inon, Sep 24):
 *
 *  1. What you look at and what you do — the view controls (row filter /
 *     division / cancelled), then the actions (Send N Invoices, Notify and the
 *     single primary Add Player Slot).
 *  2. How the team is set up — Split Evenly with its figure, then who can join
 *     (Private Team and its link). One sheet only, so All Divisions has no row 2.
 *
 * It used to be one row wrapping control by control, so it broke wherever the
 * width ran out: on a 1280px laptop Copy Team Link fell away from its own
 * Private Team switch, or the actions sat alone on a second line — and a few
 * pixels of scrollbar decided which. Fixed rows look the same at every desktop
 * width, in the admin modal and on the Team page, and keep Tab order equal to
 * reading order. On a phone each trailing group wraps under its leading one.
 *
 * There is deliberately NO search box: the Player column carries its own
 * floating filter, so a separate input was a second way to do the same thing.
 * There is also no Refresh button — the sheet refetches on every mutation, so
 * it was a manual control for something the app already does.
 */
const BalanceControls = ({
  registrations,
  rowFilter,
  onRowFilter,
  showSheetActions,
  sheetActionsPending,
  onSplitEvenly,
  evenSplitLabel,
  selectedCount,
  onSendInvoices,
  selectedRegistrationId,
  onSelectRegistration,
  hideDivisionFilter = false,
  showCancelled,
  onToggleCancelled,
  cancelledToggle,
  onAddSlots,
  addDisabled,
  onNotify,
  isPrivate = null,
  onTogglePrivacy,
  shareLink = null,
}: BalanceControlsProps) => {
  const { message } = App.useApp()
  /**
   * One dropdown instead of four loose chips. The chips read as four unrelated
   * controls competing with the toggles beside them; a single select says
   * plainly that exactly ONE row filter is active at a time.
   */
  const rowFilterOptions = [
    { label: balanceLabel('common:balanceFilterAll'), value: 'all' },
    { label: balanceLabel('common:balanceFilterOwes'), value: 'owes' },
    { label: balanceLabel('common:balanceFilterPaid'), value: 'paid' },
    { label: balanceLabel('common:balanceFilterOpen'), value: 'open' },
    // Jeff, Oct 1 — slots held for invited players who haven't registered yet.
    { label: balanceLabel('common:balanceFilterInvited'), value: 'invited' },
  ]

  const divisionOptions = [
    { label: balanceLabel('common:allDivisions'), value: ALL_DIVISIONS },
    ...registrations.map((registration) => ({
      label: registration.division.root_name
        ? `${registration.division.name} · ${registration.division.root_name}`
        : registration.division.name,
      value: registration.id,
    })),
  ]

  return (
    <div css={toolbarStyle}>
      {/* Row 1 — what you look at, then what you do. */}
      <div css={toolbarRowStyle}>
        <div css={toolbarGroupStyle}>
          {/* Quick filters — the questions a rep actually asks of this sheet
              ("who still owes me?"). ONE dropdown, not four loose chips: only one
              row filter can be active, and the chips read as unrelated controls
              competing with the toggles either side of them. */}
          {/* `width` as well as the basis: the group sizes itself from its
              children's widths, which ignore a flex-basis, so without it the
              group comes out too narrow for the select and wraps the switch. */}
          <div
            css={css`
              flex: 0 1 190px;
              width: 190px;
            `}
            data-testid='balance-row-filters'
          >
            <SelectInput
              label=''
              /* 32px — the height of the buttons sharing its row. */
              size='small'
              options={rowFilterOptions}
              value={rowFilter}
              onChange={(value) => onRowFilter(value as BalanceRowFilter)}
              labelRender={renderRowFilterLabel}
              fieldErrors={[]}
              customError={''}
              disabled={false}
              name='balance-row-filter'
            />
          </div>
          {registrations.length > 1 && !hideDivisionFilter ? (
            <div
              css={css`
                flex: 0 1 260px;
                width: 260px;
              `}
              data-testid='division-filter'
            >
              <SelectInput
                label=''
                size='small'
                options={divisionOptions}
                value={selectedRegistrationId}
                onChange={(value) => onSelectRegistration(String(value))}
                fieldErrors={[]}
                customError={''}
                disabled={false}
                name='balance-division-filter'
              />
            </div>
          ) : null}
          {/* Display Cancelled is a VIEW filter, so it sits with the other view
              controls — and it is now the toolbar's ONLY switch, which is why the
              bordered group that used to separate it from the money-affecting
              toggles is gone with them. */}
          <Tooltip
            title={
              cancelledToggle.reason === null
                ? ''
                : balanceText(
                    cancelledToggle.reason === 'none'
                      ? 'common:balanceCancelledNoneTip'
                      : 'common:balanceCancelledFilteredTip'
                  )
            }
            /*
             * SN-8346 — on the WRAPPER, and keyboard-reachable.
             *
             * A disabled antd Switch swallows pointer events, so a tooltip mounted
             * on the switch itself would never open in the one state that needs an
             * explanation. `focus` is what makes the reason reachable without a
             * mouse; hover alone would hide it from keyboard users entirely.
             *
             * It wraps the outer flex div rather than an inner span because
             * `SwitchInput` returns a FRAGMENT — its label and its Switch are two
             * children of this div's flex context, and a span between them would
             * collapse the pair into a single flex item.
             */
            trigger={['hover', 'focus']}
          >
            <div
              /*
               * A disabled antd Switch drops OUT of the tab order, so without this
               * the reason would be mouse-only. Focusing the wrapper is what puts
               * the explanation back on a keyboard path; while the switch is live it
               * takes the tab stop itself and the wrapper stays out of the way.
               */
              tabIndex={cancelledToggle.disabled ? 0 : -1}
              css={[
                switchFieldStyle,
                css`
                  white-space: nowrap;
                  &:focus-visible {
                    outline: 2px solid ${colors.PRIMARY};
                    outline-offset: 2px;
                    border-radius: 4px;
                  }
                `,
              ]}
            >
              <SwitchInput
                title={balanceLabel('common:displayCancelled')}
                value={showCancelled}
                onChange={onToggleCancelled}
                disabled={cancelledToggle.disabled}
                dataTestId='display-cancelled-switch'
              />
            </div>
          </Tooltip>
        </div>
        <div css={[toolbarGroupStyle, trailingGroupStyle, actionGroupStyle]}>
          {/* Revealed by the selection, not always present: "Send 3 Invoices"
              with nothing ticked would be a permanently disabled button, and this
              is the action James called buried. */}
          {selectedCount > 0 ? (
            <HeaderActionButton
              variant='primary'
              size='small'
              textWeight='regular'
              icon={
                <SendOutlined
                  css={css`
                    font-size: 12px;
                    color: ${PRIMARY_BUTTON_CONTENT.icon};
                  `}
                />
              }
              onClick={onSendInvoices}
              text={balanceLabel('common:balanceBatchInvoiceButton', {
                count: selectedCount,
              })}
              data-testid='send-invoices'
            />
          ) : null}
          {/* One action. Never disabled: the composer is where you find out which
              channels are available to this audience, and a dead button in the
              toolbar cannot say why. */}
          <HeaderActionButton
            variant='secondary'
            size='small'
            textWeight='regular'
            onClick={onNotify}
            icon={
              <NotificationOutlined
                css={css`
                  font-size: 13px;
                  color: ${colors.NEUTRAL_0};
                `}
              />
            }
            text={balanceLabel('common:balanceNotifyMenu')}
            data-testid='balance-notify'
          />
          <Tooltip
            title={
              addDisabled
                ? balanceText('common:balanceAddPlayerSlotDisabled')
                : ''
            }
          >
            {/* `size='small'` — the same 32px pill the Registration Setup button
                uses (Schedule > Registration), so the one primary action on this
                tab matches the primary action on the page admins reach it from.
                The 40px default existed only to match the Refresh button that sat
                beside it; with that gone, the smaller pill is the house style.
                Icon is explicitly coloured because it does not inherit the
                label's dark-on-yellow colour. */}
            {/* Inon, Oct 2 — one button, no menu: the kind of slot is a tab inside
                Add Player Slots, where each one can say what it does. */}
            <HeaderActionButton
              variant='primary'
              size='small'
              textWeight='regular'
              icon={
                <PlusOutlined
                  css={css`
                    font-size: 12px;
                    color: ${PRIMARY_BUTTON_CONTENT.icon};
                  `}
                />
              }
              disabled={addDisabled}
              onClick={onAddSlots}
              text={balanceLabel('common:balanceAddPlayerSlot')}
              data-testid='add-line-item'
            />
          </Tooltip>
        </div>
      </div>
      {/* Row 2 — how the team is set up: the split, then who can join. Every
          control in it belongs to ONE sheet, so All Divisions has no row 2. */}
      {showSheetActions ? (
        <div css={toolbarRowStyle}>
          <div css={toolbarGroupStyle}>
            {/*
              SN-4709 (James, Jul 31) — Auto-Split and Lock Sheet are gone, and with
              them the bordered "sheet settings" group they lived in.

              Auto-Split is replaced by the advisory EVEN SPLIT column plus this
              explicit action: a column cannot silently overwrite typed amounts, and a
              button that says what it will do can. Lock Sheet simply had no user —
              it named an internal freeze, not an intent (a one-shot migration released
              every sheet that was already locked).
            */}
            <Tooltip title={balanceText('common:balanceSplitEvenlyBody')}>
              <span data-testid='split-evenly-wrap'>
                <HeaderActionButton
                  variant='secondary'
                  size='small'
                  textWeight='regular'
                  onClick={onSplitEvenly}
                  disabled={sheetActionsPending}
                  text={balanceLabel('common:balanceSplitEvenly')}
                  data-testid='split-evenly'
                />
              </span>
            </Tooltip>
            {/* Beside the button that would APPLY it, not floating on its own: the
                figure is only ever interesting as "this is what Split Evenly will
                write". Quiet type, because it is a reference and not a total. */}
            {evenSplitLabel !== null ? (
              <span css={evenSplitStyle} data-testid='even-split-reference'>
                {balanceText('common:balanceEvenSplitReference', {
                  amount: evenSplitLabel,
                })}
              </span>
            ) : null}
          </div>
          {(isPrivate !== null && onTogglePrivacy) || shareLink ? (
            <div css={[toolbarGroupStyle, trailingGroupStyle]}>
              {/*
                SN-4709 Team Privacy (Jeff, Sep 1). "What happens if some dude gets on your team?"
                A private team is not listed for players to pick from; the link below is the only
                way in. The switch is the SETTING, the link is the consequence — so they sit
                together rather than in separate corners of the toolbar.
              */}
              {isPrivate !== null && onTogglePrivacy ? (
                /*
                  The TOOLTIP's span IS the flex row — not an inert span nested inside
                  one. `SwitchInput` renders its label and its `Switch` as a FRAGMENT, so
                  whatever element wraps it has to be the thing that lays them out. A
                  plain span left the label (a block-level div) on its own line with the
                  switch dropped underneath, which is why this control stood a head
                  taller than Display Cancelled beside it.
                */
                <Tooltip title="Private teams are hidden from the list players choose from. Only someone with your team's registration link can join.">
                  <span
                    css={[
                      switchFieldStyle,
                      css`
                        white-space: nowrap;
                      `,
                    ]}
                    data-testid='team-privacy-wrap'
                  >
                    <SwitchInput
                      title={capitalizeWords('Private Team')}
                      value={isPrivate}
                      onChange={onTogglePrivacy}
                      disabled={sheetActionsPending}
                      dataTestId='team-privacy-switch'
                    />
                  </span>
                </Tooltip>
              ) : null}
              {/*
                Jeff dislikes today's affordance — "that stupid thing that includes the URL that
                always carries over" (useShareRegistration rewrites the browser URL via
                history.replaceState). He asked for "copy registration link to provide to your
                users" as a plain, visible control, so this is a button that copies and says so.
              */}
              {shareLink ? (
                <HeaderActionButton
                  variant='secondary'
                  size='small'
                  textWeight='regular'
                  icon={
                    <CopyOutlined
                      css={css`
                        font-size: 12px;
                      `}
                    />
                  }
                  onClick={() => {
                    /*
                     * AWAIT the write before claiming success (round-1 review F8). An
                     * unsupported or permission-denied clipboard rejects, and reporting
                     * "copied" over the top of that tells a rep they hold a link they do not —
                     * so they paste nothing into the message they are about to send.
                     */
                    const copy = navigator.clipboard?.writeText(shareLink)
                    if (!copy) {
                      message.error(
                        'Your browser blocked the copy. Select the link and copy it manually.'
                      )
                      return
                    }
                    copy
                      .then(() =>
                        message.info(
                          'Team registration link copied to clipboard'
                        )
                      )
                      .catch(() =>
                        message.error(
                          'Your browser blocked the copy. Select the link and copy it manually.'
                        )
                      )
                  }}
                  text={capitalizeWords('Copy Team Link')}
                  data-testid='copy-team-link'
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default BalanceControls
